<script setup lang="ts">
export interface PagesData extends payloadData {
  docs: Page[]
}

const globalPageGroups = useState<any[]>('globalPageGroups')

const route = useRoute()
const { locale } = useI18n()
const { slug, path } = route.params

const runtimeConfig = useRuntimeConfig()
const previewSecret = runtimeConfig?.previewSecret || runtimeConfig?.public?.previewSecret
const isPreview = computed(() => route?.query?.previewSecret === previewSecret)

const pathClean = Array.isArray(path) ? path?.filter(i => i?.length >= 1) : path
const pathArray = Array.isArray(pathClean) ? [slug, ...pathClean] : [slug, pathClean]
const pageSlug = pathArray[pathArray.length - 1]

const isInGlobalGroups = globalPageGroups.value?.some((group: any) => {
  if (group.main?.slug !== pathArray[0]) return false
  if (pathArray.length === 1) return true
  const level = group.levels?.find((l: any) => l.main?.slug === pathArray[1])
  if (!level) return false
  if (pathArray.length === 2) return true
  return level.children?.some((c: any) => c.slug === pathArray[2])
})

if (!isInGlobalGroups) {
  throw createError({
    statusCode: 404,
    statusMessage: `Page not found`,
  })
}

const { data, error } = await useAsyncData(
  () => `page-${slug}-${pathArray.join('-')}-${locale.value}`,
  () => $fetch('/api/payload-gql', {
    method: 'POST',
    body: {
      query: SinglePageQuery,
      variables: {
        slug: { equals: pageSlug },
        locale: locale.value,
        isPreview: isPreview.value,
        // with `draft: true` payload returns each doc's latest version, whatever its
        // status -- so preview must accept both, or a doc with no pending draft
        // (latest version published) matches nothing and the page renders empty
        statuses: isPreview.value ? ['draft', 'published'] : ['published'],
      },
      isPreview: isPreview.value,
    },
  }),
  { transform: (data: PagesData) => transformPayloadData<PagesData>(data) as Page }
)

if (error.value) {
  throw createError({ statusCode: 500, statusMessage: 'Error fetching page' })
}

if (!data.value && !isPreview.value) {
  throw createError({ statusCode: 404, statusMessage: 'Page not found' })
}

usePageSeo({
  title: () => data.value?.title,
})

const pageNav = computed(() => usePageNavigation(data.value?.content))
</script>

<template>
  <page-layout>
    <template #header>
      <page-header
        :title="data?.title"
        :route="{
          name: 'slug-path',
          title: data?.title,
          slug: slug,
        }"
        :include-breadcrumbs="true"
        :page-nav="pageNav"
      />
    </template>

    <template #default>
      <pages-slug
        v-if="data"
        :data="data"
      />
    </template>
  </page-layout>
</template>
